import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { DynamicField, DynamicValuesType, Exception, PaymentDetailType } from '@cargos/sprintpay-models';
import { PaymentsService } from '@cargos/sprintpay-services';
import { filter, finalize, map, switchMap, take } from 'rxjs';
import { Detail } from 'src/app/shared/models/details';
import { CurrentFacilityService, CustomerService, DynamicsFieldsService } from 'src/app/shared/services';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-payment-details',
    templateUrl: './payment-details.component.html',
    standalone: false,
})
export class PaymentDetailsComponent implements OnInit, OnDestroy {
    public paymentDetails: PaymentDetailType[] = [];
    public paymentDetailsSection: Detail[][] = [];
    public paymentCustomerReference: Detail[][] = [];
    public customerInformation: Detail[];
    public paymentId: string | null;
    public isLoading: boolean = true;
    public facilityName: string;
    public files: string[] = [];
    public dynamicFields: Detail[] | null = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private matDialog: MatDialog,
        private paymentsService: PaymentsService,
        private customerService: CustomerService,
        private currentFacilityService: CurrentFacilityService,
        private dynamicsFieldsService: DynamicsFieldsService
    ) {}

    ngOnInit(): void {
        this.facilityName = this.currentFacilityService.getFacilitySource();
        this.paymentId = this.activatedRoute.snapshot.paramMap.get('id');
        this.loadPaymentDetails();
    }

    ngOnDestroy(): void {
        this.matDialog.closeAll();
    }

    private loadPaymentDetails(): void {
        this.isLoading = true;
        const companyType: string = this.customerService.getCompanyName() ?? '';
        this.paymentsService
            .getPaymentDetails(this.paymentId ?? '')
            .pipe(
                take(1),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (paymentDetails) => {
                    if (Array.isArray(paymentDetails)) {
                        paymentDetails.forEach((payment) => {
                            payment.companyType = companyType;
                            this.paymentDetails.push(payment);

                            if (payment?.attachments) {
                                this.files.push(payment?.attachments);
                            }
                        });
                    }

                    this.getCustomerInformationSection();
                    this.getPaymentDetailsSection();
                    this.loadDynamicFields();
                },
                error: (error: Exception) => {
                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: error.type,
                    });
                },
            });
    }

    private getPaymentDetailsSection(): void {
        const paymentDetailsMethod = this.customerService.getCustomerPaymentDetailsFieldsBuilder();
        this.paymentDetailsSection = this.paymentDetails.map((paymentDetail) =>
            paymentDetailsMethod(paymentDetail, paymentDetail.facility!, paymentDetail.id.toString())
        );
    }

    private getCustomerInformationSection(): void {
        const payment = this.paymentDetails?.find((payment: PaymentDetailType) => payment.notificationEmail);
        const paymentCustomerInformationMethod = this.customerService.getCustomerInformationFieldsBuilder();
        if (payment) {
            this.customerInformation = paymentCustomerInformationMethod(payment);
        }
    }

    private loadDynamicFields(): void {
        this.dynamicsFieldsService
            .getLoadingDynamicFields()
            .pipe(
                filter((loading) => !loading),
                take(1),
                switchMap(() =>
                    this.dynamicsFieldsService.getDynamicFieldsBySection$(1).pipe(
                        take(1),
                        map((dynamicFields) =>
                            this.paymentDetails.length
                                ? this.getPaymentDynamicFieldsAndValues(this.paymentDetails[0], dynamicFields)
                                : []
                        )
                    )
                )
            )
            .subscribe({
                next: (formattedDynamicFields) => {
                    this.dynamicFields = formattedDynamicFields;
                },
                error: (error) => {
                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: 'error',
                    });
                },
            });
    }

    private getPaymentDynamicFieldsAndValues(
        payment: PaymentDetailType,
        dynamicFields: DynamicField[] | null
    ): Detail[] {
        if (!dynamicFields || !dynamicFields?.length) {
            return [];
        }

        const dynamicValues = payment?.dynamicValues as DynamicValuesType[];

        const fields = dynamicFields.map((field) => {
            const value = dynamicValues.find((value) => field.id === value.dynamicField?.id);

            return {
                label: field.label,
                value: value?.value ?? 'N/A',
                show: !field.isReplacement,
            };
        });

        return fields;
    }
}
